import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  UserPlus, Gift, Check, ArrowRight,
  Zap, Shield, Eye, EyeOff, Bell
} from 'lucide-react';
import { formatErrorMessage } from '../utils/errorHandler';
import axios from 'axios';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const referralCode = searchParams.get('ref');
  const planFromUrl = searchParams.get('plan');
  const billingFromUrl = searchParams.get('billing');

  useEffect(() => {
    if (referralCode) {
      validateReferralCode(referralCode);
      trackClick(referralCode);
    }
  }, [referralCode]);

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`/referral/validate/${code}`);
      if (response.data.valid) {
        setReferralInfo({
          code: code,
          partnerName: response.data.referrer_name,
          discountPercent: response.data.discount_percent || 15
        });
      }
    } catch (error) {
      console.error('Referral validation error:', error);
    }
  };

  const trackClick = async (code) => {
    try {
      await axios.post(`/referral/track-click/${code}`);
    } catch (error) {
      console.error('Click track error:', error);
    }
  };

  const validatePassword = (pwd) => {
    if (pwd.length < 6) {
      return 'Şifre en az 6 karakter olmalı';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (newPassword) {
      setPasswordError(validatePassword(newPassword));
    } else {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name, referralCode);
      toast.success('Hesabınız oluşturuldu!');
      
      // Eğer Pro plan seçilmişse ödeme sayfasına yönlendir
      if (planFromUrl === 'pro' && billingFromUrl) {
        navigate(`/payment?plan=pro&billing=${billingFromUrl}${referralCode ? `&ref=${referralCode}` : ''}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-start justify-center px-4 pt-4 md:pt-8">
      <div className="w-full max-w-md">
        
        {/* Referral Info (if exists) */}
        {referralInfo && (
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2">
              <Gift className="h-4 w-4 text-primary" />
              <span className="text-sm text-primary">
                {referralInfo.partnerName} davet etti • %{referralInfo.discountPercent} indirim
              </span>
            </div>
          </div>
        )}

        {/* Register Card */}
        <Card className="border-slate-800 bg-slate-900/50">
          <CardHeader className="text-center pb-2 pt-4">
            <CardTitle className="text-2xl">Ücretsiz Başla</CardTitle>
            <CardDescription>
              Yapay zeka destekli sinyalleri incele. <span className="text-primary">Kredi kartı gerekmez.</span>
            </CardDescription>
            {/* Trust badges */}
            <div className="flex justify-center gap-4 mt-2">
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Zap className="h-3 w-3 text-primary" />
                10 saniyede kayıt
              </span>
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Shield className="h-3 w-3 text-primary" />
                Güvenli
              </span>
            </div>
          </CardHeader>
          
          <CardContent className="pt-2">
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="name">Ad Soyad</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Adınız Soyadınız"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-slate-800/50 border-slate-700"
                  data-testid="register-name-input"
                />
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="email">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-800/50 border-slate-700"
                  data-testid="register-email-input"
                />
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="password">Şifre</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    className={`bg-slate-800/50 border-slate-700 pr-10 ${passwordError ? 'border-red-500' : ''}`}
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-xs text-red-400">{passwordError}</p>
                )}
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="confirmPassword">Şifre Tekrar</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-slate-800/50 border-slate-700 pr-10"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 text-black font-semibold h-11"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Oluşturuluyor...
                  </div>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Ücretsiz Hesap Oluştur
                  </>
                )}
              </Button>
            </form>

            {/* Ücretsiz Özellikler */}
            <div className="mt-6 pt-6 border-t border-slate-800">
              <p className="text-xs text-slate-500 mb-3 text-center">Ücretsiz hesapla:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2 text-slate-400">
                  <Check className="h-3 w-3 text-green-500" />
                  <span>Tüm sinyalleri gör</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Check className="h-3 w-3 text-green-500" />
                  <span>Performans takibi</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Check className="h-3 w-3 text-green-500" />
                  <span>Geçmiş işlemler</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Check className="h-3 w-3 text-green-500" />
                  <span>AI analizleri</span>
                </div>
              </div>
            </div>

            {/* Login Link */}
            <p className="text-center text-sm text-slate-400 mt-6">
              Zaten hesabın var mı?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Giriş Yap
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="flex justify-center gap-6 mt-6 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Shield className="h-3 w-3" />
            <span>Güvenli</span>
          </div>
          <div className="flex items-center gap-1">
            <Zap className="h-3 w-3" />
            <span>Hızlı Kayıt</span>
          </div>
          <div className="flex items-center gap-1">
            <Check className="h-3 w-3" />
            <span>Ücretsiz Başla</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
